import LoadingSpinner from "@/components/common/loading-spinner";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { buildMetadata } from "@/lib/metadata";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { GoogleIcon, MicrosoftIcon, ZohoIcon } from "./-icons/icons";

const fallback = "/" as const;

export const Route = createFileRoute("/_auth/sign-in")({
  component: RouteComponent,
  validateSearch: z.object({
    redirect: z.string().optional().catch(""),
  }),
  beforeLoad: async ({ context, search }) => {
    const authenticated = await context.auth.checkAuthentication();
    if (authenticated) {
      throw redirect({ to: search.redirect || fallback });
    }
  },
  head: () => ({
    meta: buildMetadata({
      title: "OpenFarming - AI for Distributors",
      description: "Sign in to your account",
      keywords: ["sign in", "account", "login"],
    }),
  }),
});

export const signInSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(4, "Password must be at least 4 characters long"),
});

function RouteComponent() {
  const { isLoading, signIn, attachProvider } = useAuth();

  const form = useForm({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  return (
    <div className="space-y-10">
      <div className="space-y-2">
        <h1 className="font-medium text-5xl">Sign In</h1>
        <span>Please sign in to continue.</span>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(async (data) => {
            await signIn(data.username, data.password);
          })}
          className="space-y-6"
        >
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isLoading} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input {...field} type="password" disabled={isLoading} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button className="w-full" type="submit" disabled={isLoading}>
            {isLoading ? <LoadingSpinner /> : "Sign in"}
          </Button>
        </form>
      </Form>

      <div className="flex w-full items-center gap-2">
        <Separator className="flex-1" />
        <span className="font-medium text-muted-foreground">or</span>
        <Separator className="flex-1" />
      </div>

      <div className="space-y-2">
        <Button
          className="w-full"
          variant="outline"
          disabled={isLoading}
          onClick={() => attachProvider("google", "login")}
        >
          <GoogleIcon />
          Sign in with Google
        </Button>
        <Button
          className="w-full"
          variant="outline"
          disabled={isLoading}
          onClick={() => attachProvider("microsoft", "login")}
        >
          <MicrosoftIcon />
          Sign in with Microsoft
        </Button>
        <Button
          className="w-full"
          variant="outline"
          disabled={isLoading}
          onClick={() => attachProvider("zoho", "login")}
        >
          <ZohoIcon />
          Sign in with Zoho
        </Button>
      </div>

      <div className="mx-auto w-fit text-muted-foreground text-sm">
        Need an account?{" "}
        <Link
          to="/sign-up"
          className={cn(
            buttonVariants({ variant: "link" }),
            "w-fit px-0 underline",
          )}
        >
          Sign up
        </Link>
      </div>
    </div>
  );
}
